/**
 * Load mode: scan each pot as it goes into the car. The day's load list (planned pots from
 * stock + spares) ticks itself off; a pot that isn't on the list is caught before she drives.
 * Loading is part of the plan (intent), not the placement record.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Check, ChevronLeft, PackageCheck, TriangleAlert, Undo2 } from "lucide-react";
import type { Catalog, PlannedDay } from "@hitl/rotations-domain";
import type { PlannerStore } from "../state/plannerStore";
import { markLoaded, unmarkLoaded } from "../state/plannerState";
import { parsePotCode } from "../state/potCode";
import { createStabilizer } from "../state/stabilizer";
import { useQrScanner, type CameraStatus } from "../scan/useQrScanner";
import { CameraNotice, RailButton, RailIcons } from "../scan/parts";
import { TypeNumberSheet } from "../scan/sheets";
import { formatDay } from "../scan/copy";
import { weekdayName } from "./PlanScreen";
import { spareFitsLine } from "./spareFits";

export interface LoadFixture {
  readonly camera?: CameraStatus;
  readonly wrongPot?: string;
  /** Show the "just scanned" confirmation for this pot. */
  readonly lastIn?: string;
}

export function LoadView(props: {
  catalog: Catalog;
  day: PlannedDay;
  routeName: string;
  loaded: readonly string[];
  store: PlannerStore;
  backLabel?: string;
  onBack: () => void;
  fixture?: LoadFixture;
}) {
  const { catalog, day, routeName, loaded, store, fixture } = props;
  const [notice, setNotice] = useState<{ kind: "wrong"; potId: string } | { kind: "again"; potId: string } | { kind: "foreign" } | null>(
    fixture?.wrongPot ? { kind: "wrong", potId: fixture.wrongPot } : null,
  );
  const [typing, setTyping] = useState(false);
  // What just happened, readable at arm's length: the pot that just went in, and an Undo.
  const [lastIn, setLastIn] = useState<string | null>(fixture?.lastIn ?? null);
  const [toast, setToast] = useState<{ id: number; potId: string } | null>(
    fixture?.lastIn ? { id: 1, potId: fixture.lastIn } : null,
  );
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast((cur) => (cur?.id === toast.id ? null : cur)), 6000);
    return () => clearTimeout(t);
  }, [toast]);
  useEffect(() => {
    if (!lastIn || fixture?.lastIn) return;
    const t = setTimeout(() => setLastIn(null), 3000);
    return () => clearTimeout(t);
  }, [lastIn, fixture?.lastIn]);
  const stabilizer = useRef(createStabilizer());
  const keyboardProxy = useRef<HTMLInputElement>(null);

  // Driving order: the pot for the first stop first, spares last.
  const planned = day.report.swaps.filter((s) => s.source === "stock" && s.potId).map((s) => s.potId!);
  const list = [...planned.filter((p) => day.report.loadList.includes(p)), ...day.report.loadList.filter((p) => !planned.includes(p))];
  const purpose = (potId: string): { text: string; none?: boolean } => {
    const swap = day.report.swaps.find((s) => s.potId === potId && s.source === "stock");
    if (swap) {
      const slot = catalog.slot(swap.slotId);
      return { text: `${catalog.client(slot.clientId)?.name}, ${slot.label}` };
    }
    const spare = day.report.spares.find((s) => s.potId === potId);
    if (!spare) return { text: "Spare" };
    const fits = spareFitsLine(catalog, spare, day.plan.swaps.map((s) => s.slotId));
    return { text: `Spare · ${fits.text.charAt(0).toLowerCase()}${fits.text.slice(1)}`, none: fits.none };
  };
  const extras = loaded.filter((p) => !list.includes(p));
  const doneCount = list.filter((p) => loaded.includes(p)).length;
  const allIn = list.length > 0 && doneCount === list.length;

  const handle = useCallback(
    (raw: string) => {
      const code = parsePotCode(raw);
      if (code.kind === "foreign") return setNotice({ kind: "foreign" });
      if (loaded.includes(code.potId)) return setNotice({ kind: "again", potId: code.potId });
      if (!list.includes(code.potId)) return setNotice({ kind: "wrong", potId: code.potId });
      setNotice(null);
      store.update((s) => markLoaded(s, day.on, code.potId));
      setLastIn(code.potId);
      setToast({ id: Date.now(), potId: code.potId });
    },
    [loaded, list, store, day.on],
  );

  const onDecode = useCallback(
    (text: string) => {
      if (typing) return;
      const accepted = stabilizer.current.feed(text, performance.now());
      if (!accepted) return;
      handle(accepted);
      // Let the same sticker be scanned again a moment later (e.g. after un-ticking it).
      setTimeout(() => stabilizer.current.reset(), 1500);
    },
    [typing, handle],
  );
  const scanner = useQrScanner(onDecode);
  const camera = fixture?.camera ? { ...scanner, status: fixture.camera } : scanner;

  const openTyping = () => {
    keyboardProxy.current?.focus({ preventScroll: true });
    setTyping(true);
  };

  return (
    <main className="load">
      {toast && (
        <div className="toast load__toast" role="status" key={toast.id}>
          <span>{toast.potId} is in the car</span>
          <button
            className="toast__undo"
            onClick={() => {
              store.update((s) => unmarkLoaded(s, day.on, toast.potId));
              setLastIn(null);
              setToast(null);
            }}
          >
            <Undo2 aria-hidden size={18} strokeWidth={2.5} />
            Undo
          </button>
        </div>
      )}
      <input ref={keyboardProxy} className="kb-proxy" aria-hidden tabIndex={-1} autoCapitalize="characters" autoComplete="off" />
      <div className="load__camera">
        <video ref={camera.videoRef} className="scan__video" playsInline muted aria-hidden />
        {camera.status === "live" && !lastIn && <div className="frame load__frame" aria-hidden />}
        {lastIn && (
          <div className="load__scanned" aria-hidden>
            <span className="chip chip--in">
              <Check size={20} strokeWidth={2.75} />
              In the car
            </span>
            <span className="load__scanned-pot">{lastIn}</span>
          </div>
        )}
        <nav className="load__rail" aria-label="Actions">
          <RailButton label="Type number" icon={RailIcons.Keyboard} onClick={openTyping} />
        </nav>
        <header className="load__top">
          <button className="plan__back" onClick={props.onBack}>
            <ChevronLeft aria-hidden size={22} />
            {props.backLabel ?? "Plan"}
          </button>
        </header>
        <CameraNotice status={camera.status} onStart={camera.start} onType={openTyping} />
      </div>

      <section className="load__panel" aria-live="polite">
        <h1 className="load__title">
          Load {weekdayName(day.on)}'s car
          <small>
            {routeName} · {formatDay(day.on)}
          </small>
        </h1>
        <p className="load__count" role="status">
          <b>{doneCount}</b> of {list.length} in the car
        </p>
        <div className="load__progress" aria-hidden>
          {list.map((p) => (
            <span key={p} className={loaded.includes(p) ? "is-in" : ""} />
          ))}
        </div>

        {notice?.kind === "wrong" && (
          <div className="load__notice" role="alert">
            <TriangleAlert aria-hidden size={18} />
            <span>{notice.potId} isn't on {weekdayName(day.on)}'s list.</span>
            <button
              onClick={() => {
                store.update((s) => markLoaded(s, day.on, notice.potId));
                setNotice(null);
              }}
            >
              Add as extra
            </button>
            <button onClick={() => setNotice(null)}>Ignore</button>
          </div>
        )}
        {notice?.kind === "again" && <p className="load__quiet">{notice.potId} is already ticked.</p>}
        {notice?.kind === "foreign" && <p className="load__quiet">That code isn't a Rotations sticker.</p>}

        {allIn && (
          <div className="load__done">
            <PackageCheck aria-hidden size={24} />
            Car loaded. Everything for {weekdayName(day.on)} is in.
          </div>
        )}

        <ul className="load__list">
          {list.map((potId) => {
            const isIn = loaded.includes(potId);
            const why = purpose(potId);
            return (
              <li key={potId}>
                <button
                  className={`load__row${isIn ? " is-in" : ""}`}
                  aria-pressed={isIn}
                  onClick={() => store.update((s) => (isIn ? unmarkLoaded(s, day.on, potId) : markLoaded(s, day.on, potId)))}
                >
                  <span className="load__tick">{isIn && <Check aria-hidden size={18} strokeWidth={3} />}</span>
                  <span className="load__pot">{potId}</span>
                  <span className={`load__for${why.none ? " is-check" : ""}`}>{why.text}</span>
                </button>
              </li>
            );
          })}
          {extras.map((potId) => (
            <li key={potId}>
              <button className="load__row is-in is-extra" onClick={() => store.update((s) => unmarkLoaded(s, day.on, potId))}>
                <span className="load__tick">
                  <Check aria-hidden size={18} strokeWidth={3} />
                </span>
                <span className="load__pot">{potId}</span>
                <span className="load__for">Extra, not on the list</span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      {typing && (
        <TypeNumberSheet
          onClose={() => setTyping(false)}
          onSubmit={(v) => {
            setTyping(false);
            handle(v);
          }}
        />
      )}
    </main>
  );
}
